// VideoBox.jsx — Reliability hardened recording pipeline (final sync + all-promises fixes)
import React, { useRef, useEffect, useState } from "react";
import {
  uploadChunkWithRetry,
  finalizeUpload,
  sendFrameToBackend,
} from "../utils/api";

const VideoBox = ({
  stop,
  interviewID,
  onFinalizeComplete,
  uploadStream,
  previewStream,
  onProctoringEvent,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const frameIntervalRef = useRef(null);
  const chunksRef = useRef(new Map());
  const uploadPromisesRef = useRef(new Map());
  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);
  const recordingActionsRef = useRef({});
  const callbacksRef = useRef({ onFinalizeComplete, onProctoringEvent });
  callbacksRef.current = { onFinalizeComplete, onProctoringEvent };
  const eventTimeoutRef = useRef(null);
  const finalizationRequestedRef = useRef(false);
  const finalizationPromiseRef = useRef(null);
  const finalizationCompleteRef = useRef(false);
  const [status, setStatus] = useState("Idle");
  const [alertMsg, setAlertMsg] = useState("");
  const [shareLink, setShareLink] = useState("");
  const fileNameRef = useRef(null);

  useEffect(() => {
    if (!uploadStream) {
      setStatus("No upload stream available");
      return;
    }
    const videoElement = videoRef.current;

    const initRecording = () => {
      if (videoRef.current && previewStream) {
        videoRef.current.srcObject = previewStream;
        videoRef.current.play().catch(console.warn);
      }

      const fileName = `${interviewID}.webm`;
      fileNameRef.current = fileName;

      chunkIndexRef.current = 0;
      chunksRef.current = new Map();
      uploadPromisesRef.current = new Map();
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
      retryAttemptRef.current = 0;
      finalizationRequestedRef.current = false;
      finalizationPromiseRef.current = null;
      finalizationCompleteRef.current = false;
      console.log(
        `🎥 RECORDING START | interview=${interviewID} | file=${fileName}`,
      );
      setStatus("Recording screen...");

      const recorder = new MediaRecorder(uploadStream, {
        mimeType: "video/webm;codecs=vp8,opus",
        videoBitsPerSecond: 1000000,
        audioBitsPerSecond: 128000,
      });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = async (e) => {
        if (e.data?.size > 0) {
          const idx = chunkIndexRef.current++;
          chunksRef.current.set(idx, {
            blob: e.data,
            uploaded: false,
            lastError: null,
          });
          const sizeMB = (e.data.size / 1024 / 1024).toFixed(2);
          console.log(
            `📦 CHUNK CREATED | interview=${interviewID} | chunk=${idx} | size=${sizeMB}MB`,
          );

          recordingActionsRef.current.uploadChunk(idx);
        }
      };

      recorder.onstop = async () => {
        finalizationRequestedRef.current = true;
        console.log(
          `🛑 RECORDER STOPPED | interview=${interviewID} | chunks_created=${chunkIndexRef.current}`,
        );
        await recordingActionsRef.current.finalizeRecording();
      };

      recorder.start(5000);

      frameIntervalRef.current = setInterval(async () => {
        try {
          const result = await sendFrameToBackend({
            videoRef,
            canvasRef,
            sessionId: interviewID,
          });

          if (result?.event) {
            setStatus(`Event: ${result.message}`);
            setAlertMsg(result.message);

            if (callbacksRef.current.onProctoringEvent && result.eventData) {
              callbacksRef.current.onProctoringEvent(result.eventData);
            }

            clearTimeout(eventTimeoutRef.current);

            eventTimeoutRef.current = setTimeout(() => {
              setStatus("Recording screen...");
              setAlertMsg("");
            }, 3500);
          }
        } catch (err) {
          console.error("Frame send error:", err);
        }
      }, 4000);
    };

    initRecording();
    const handleOnline = () => {
      if (finalizationCompleteRef.current) return;
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
      retryAttemptRef.current = 0;
      void recordingActionsRef.current.retryPendingChunks().then((allUploaded) => {
        if (finalizationRequestedRef.current) {
          return recordingActionsRef.current.finalizeRecording();
        }
        if (!allUploaded) recordingActionsRef.current.schedulePendingChunkRetry();
        return undefined;
      });
    };
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
      stopRecording();
      clearTimeout(eventTimeoutRef.current);
      if (videoElement) videoElement.srcObject = null;
    };
  }, [uploadStream, previewStream, interviewID]);

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    clearInterval(frameIntervalRef.current);
  };

  const schedulePendingChunkRetry = () => {
    if (finalizationCompleteRef.current || retryTimerRef.current) return;
    const delay = Math.min(1000 * 2 ** retryAttemptRef.current, 30000);
    retryAttemptRef.current += 1;
    retryTimerRef.current = setTimeout(async () => {
      retryTimerRef.current = null;
      if (navigator.onLine === false) return;
      const allUploaded = await retryPendingChunks();
      if (allUploaded) {
        retryAttemptRef.current = 0;
        if (finalizationRequestedRef.current) await finalizeRecording();
      } else {
        schedulePendingChunkRetry();
      }
    }, delay);
  };

  const uploadChunk = (index) => {
    const chunk = chunksRef.current.get(index);
    if (!chunk || chunk.uploaded) return Promise.resolve(Boolean(chunk?.uploaded));
    const activeUpload = uploadPromisesRef.current.get(index);
    if (activeUpload) return activeUpload;

    const uploadPromise = (async () => {
      try {
        const formData = new FormData();
        formData.append("session_id", interviewID);
        formData.append("chunk_index", index);
        formData.append("file", chunk.blob, `chunk_${index}.webm`);
        console.log(
          `⬆️ CHUNK UPLOAD START | interview=${interviewID} | chunk=${index}`,
        );
        await uploadChunkWithRetry(formData, fileNameRef.current, index);
        chunk.uploaded = true;
        chunk.lastError = null;
        console.log(
          `✅ CHUNK UPLOAD SUCCESS | interview=${interviewID} | chunk=${index}`,
        );
        return true;
      } catch (error) {
        chunk.lastError = error;
        console.error(
          `❌ CHUNK UPLOAD FAILED | interview=${interviewID} | chunk=${index}; retaining data for retry`,
          error,
        );
        setStatus(`Chunk ${index} upload pending; will retry when connected.`);
        schedulePendingChunkRetry();
        return false;
      } finally {
        uploadPromisesRef.current.delete(index);
      }
    })();
    uploadPromisesRef.current.set(index, uploadPromise);
    return uploadPromise;
  };

  const retryPendingChunks = async (indexes = null) => {
    const requestedIndexes = indexes || [...chunksRef.current.keys()];
    const pending = requestedIndexes.filter((index) => {
      const chunk = chunksRef.current.get(index);
      return chunk && !chunk.uploaded;
    });
    if (pending.length === 0) return true;
    const results = await Promise.all(pending.map((index) => uploadChunk(index)));
    return results.every(Boolean);
  };

  const finalizeRecording = async () => {
    if (finalizationCompleteRef.current || !finalizationRequestedRef.current) return;
    if (finalizationPromiseRef.current) return finalizationPromiseRef.current;

    finalizationPromiseRef.current = (async () => {
      if (chunkIndexRef.current === 0) {
        setStatus("No chunks recorded.");
        finalizationCompleteRef.current = true;
        callbacksRef.current.onFinalizeComplete?.();
        return;
      }
      if (navigator.onLine === false) {
        setStatus("Recording upload paused offline; reconnect to resume.");
        schedulePendingChunkRetry();
        return;
      }

      setStatus("Checking and uploading recording chunks...");
      try {
        for (let pass = 0; pass < 4; pass += 1) {
          await retryPendingChunks();
          const failedIndexes = [...chunksRef.current.entries()]
            .filter(([, chunk]) => !chunk.uploaded)
            .map(([index]) => index);
          if (failedIndexes.length > 0) {
            setStatus(
              `Waiting to upload recording chunks: ${failedIndexes.join(", ")}. Reconnect to retry.`,
            );
            schedulePendingChunkRetry();
            return;
          }

          const response = await finalizeUpload({
            sessionId: interviewID,
            fileName: fileNameRef.current,
            totalChunks: chunkIndexRef.current,
          });
          if (response?.status === "missing_chunks") {
            const missing = response.missing_chunks || [];
            const unavailable = missing.filter((index) => !chunksRef.current.has(index));
            if (unavailable.length > 0) {
              setStatus(
                `Recording cannot be finalized; source data is unavailable for chunks: ${unavailable.join(", ")}.`,
              );
              return;
            }
            missing.forEach((index) => {
              chunksRef.current.get(index).uploaded = false;
            });
            console.warn(
              `Backend reports missing recording chunks; retrying: ${missing.join(", ")}`,
            );
            await retryPendingChunks(missing);
            continue;
          }

          console.log(
            `✅ FINALIZE RESPONSE | interview=${interviewID} | status=${response?.status}`,
          );
          if (response?.status === "processing") {
            setStatus("Recording uploaded. Processing in background...");
            setShareLink(response.video_url || "");
          } else if (response?.status === "ready") {
            setStatus("Upload complete!");
            setShareLink(response.video_url || "");
          } else {
            const videoUrl = response?.video_url || response?.videoUrl;
            if (!videoUrl) {
              throw new Error(
                `Unexpected finalization response: ${response?.status || "missing status"}`,
              );
            }
            setStatus("Upload complete!");
            setShareLink(videoUrl);
          }
          finalizationCompleteRef.current = true;
          callbacksRef.current.onFinalizeComplete?.();
          return;
        }
        setStatus("Recording is still missing uploaded chunks; reconnect to retry.");
        schedulePendingChunkRetry();
      } catch (error) {
        console.error("Finalize error; recording data retained for retry:", error);
        setStatus("Recording finalization pending; reconnect to retry.");
        schedulePendingChunkRetry();
      } finally {
        finalizationPromiseRef.current = null;
      }
    })();
    return finalizationPromiseRef.current;
  };

  recordingActionsRef.current = {
    uploadChunk,
    retryPendingChunks,
    finalizeRecording,
    schedulePendingChunkRetry,
  };

  useEffect(() => {
    if (stop) stopRecording();
  }, [stop]);

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center bg-gray-900 overflow-hidden p-2">
      <canvas ref={canvasRef} width={640} height={480} className="hidden" />

      <div className="flex-1 w-full max-w-full rounded-xl overflow-hidden shadow-lg border-2 border-teal-600 dark:border-teal-500 hover:border-teal-400 dark:hover:border-teal-300 transition-all duration-300 group relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover group-hover:brightness-105 transition-all duration-300"
        />

        <div
          className={`absolute top-2 left-2 md:top-4 md:left-4 flex items-center gap-1.5 md:gap-2 backdrop-blur-md px-2 md:px-3 py-1 md:py-2 rounded-lg border shadow-lg font-medium ${
            status.includes("Event:")
              ? "bg-red-100/90 dark:bg-red-950/60 border-red-400 dark:border-red-600 text-red-700 dark:text-red-300"
              : "bg-gray-800/80 dark:bg-gray-900/80 border-teal-400 dark:border-teal-300 text-teal-300 dark:text-teal-200"
          }`}
        >
          <div
            className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full ${
              status.includes("Event:")
                ? "bg-red-600 animate-pulse"
                : status === "Recording screen..."
                  ? "bg-red-500 animate-pulse"
                  : status === "Upload complete!"
                    ? "bg-green-500"
                    : "bg-yellow-500 animate-pulse"
            }`}
          ></div>
          <span className="text-xs font-semibold whitespace-nowrap">
            {status.includes("Event:")
              ? "⚠️ Alert"
              : status === "Recording screen..."
                ? "🔴 Rec"
                : status}
          </span>
        </div>
      </div>

      <div className="w-full mt-2 md:mt-4 flex flex-col items-center gap-1.5 md:gap-2">
        <div
          className={`border rounded-lg px-3 md:px-4 py-2 md:py-3 w-full shadow-md transition-all ${
            status.includes("Event:")
              ? "bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-700"
              : "bg-gray-800 border-gray-700 dark:border-gray-600"
          }`}
        >
          <p
            className={`text-xs md:text-sm font-medium text-center ${
              status.includes("Event:")
                ? "text-red-700 dark:text-red-300"
                : "text-gray-300 dark:text-gray-300"
            }`}
          >
            {status}
          </p>
        </div>

        {alertMsg && (
          <div className="bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-700 rounded-lg px-3 md:px-4 py-2 md:py-2.5 w-full shadow-md animate-pulse">
            <p className="text-xs font-semibold text-red-700 dark:text-red-300 text-center flex items-center justify-center gap-2">
              {alertMsg}
            </p>
          </div>
        )}

        {shareLink && (
          <a
            href={shareLink}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-semibold text-xs shadow-md transition-all duration-300 transform hover:scale-105 active:scale-95"
          >
            📹 View Recording
          </a>
        )}
      </div>
    </div>
  );
};

export default VideoBox;
